import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { ExtendedGridColDef } from 'types/datagrid';
import {
  renderQuantity,
  RenderWithCopyButton,
} from 'ui-component/DataGrid/Render';
import { RenderStringWithIcon } from 'ui-component/DataGrid/Render/RenderStringWithIcon';
import { PositiveNumberFilterOperators } from 'ui-component/DataGrid/FilterOperators';
import { ALL_APPS } from 'constants/appConstants';
import {
  AllocationReasonCode,
  DecoratedStockLot,
} from 'ui-component/AllocatedPartsDatagrid/types';
import { Tooltip, Box } from '@mui/material';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';

const TEAM_RELATED_REASON_CODES = [
  AllocationReasonCode.TEAM_MISMATCH,
  AllocationReasonCode.TEAM_NULL_STOCK_LOT_ASSIGNED,
  AllocationReasonCode.TEAM_NULL_KIT_UNASSIGNED,
  AllocationReasonCode.TEAM_MATCH,
];

const isTeamRelatedReason = (reasonCode: AllocationReasonCode): boolean =>
  TEAM_RELATED_REASON_CODES.includes(reasonCode);

export const getStockLotColumns = (
  totalQuantity: number,
  handleReplaceWithCustomTeamName: (inputString: string) => string
): ExtendedGridColDef<DecoratedStockLot>[] => [
  {
    field: 'lotId',
    headerName: 'Lot ID',
    description: 'Cofactr lot identifier',
    type: 'string',
    hideable: true,
    minWidth: 120,
    flex: 1,
    renderCell: (params: GridRenderCellParams<string, DecoratedStockLot>) =>
      RenderWithCopyButton(params.value || '-', params.value || '', 'lot ID'),
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'hasAllocation',
    headerName: 'Allocated',
    description:
      'Whether this stock lot has an allocation for this kit request line',
    type: 'boolean',
    hideable: true,
    minWidth: 120,
    flex: 0.7,
    renderCell: (params: GridRenderCellParams<boolean, DecoratedStockLot>) => {
      const hasAllocation = params.row.hasAllocation;
      const AllocationIcon = hasAllocation ? IconCircleCheck : IconCircleX;
      const color = hasAllocation ? 'success' : 'grey';
      const label = hasAllocation ? 'Allocated' : 'Not Allocated';

      return RenderStringWithIcon({
        value: label,
        Icon: AllocationIcon,
        color,
        tooltip: label,
        typographyVariant: 'subtitle1',
      });
    },
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'allocationReason',
    headerName: 'Reason',
    description: 'Reason for stock lot selection or exclusion',
    type: 'string',
    hideable: true,
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => params.row.allocationReason?.reason || '-',
    renderCell: (params: GridRenderCellParams<string, DecoratedStockLot>) => {
      const allocationReason = params.row.allocationReason;
      if (!allocationReason) return '-';

      const isTeamRelated = isTeamRelatedReason(allocationReason.reasonCode);
      const replaceTeamName = (text: string) =>
        isTeamRelated ? handleReplaceWithCustomTeamName(text) : text;

      const displayReason = replaceTeamName(allocationReason.reason);
      const hasMultipleReasons =
        allocationReason.allReasons && allocationReason.allReasons.length > 1;

      if (hasMultipleReasons) {
        const tooltipContent = (
          <Box>
            <Box sx={{ fontWeight: 'bold', mb: 1 }}>
              All applicable reasons:
            </Box>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {allocationReason.allReasons?.map(
                (reason: string, index: number) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{ mb: 0.5, '&::before': { content: '"• "' } }}
                  >
                    {replaceTeamName(reason)}
                  </Box>
                )
              )}
            </Box>
          </Box>
        );

        return (
          <Tooltip title={tooltipContent} arrow placement="top">
            <Box sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
              {displayReason}
            </Box>
          </Tooltip>
        );
      }

      return displayReason;
    },
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'allocatedQuantity',
    headerName: 'Amount Allocated',
    description: 'Quantity of this stock lot allocated to this kit request',
    type: 'number',
    hideable: true,
    minWidth: 140,
    flex: 0.7,
    renderCell: (params: GridRenderCellParams<number, DecoratedStockLot>) =>
      renderQuantity(params.row.allocatedQuantity, true),
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
  {
    field: 'quant',
    headerName: `Quantity (Total: ${totalQuantity.toLocaleString()})`,
    description: 'Current unit quantity in this stock lot',
    type: 'number',
    hideable: true,
    minWidth: 100,
    flex: 0.5,
    filterOperators: PositiveNumberFilterOperators,
    renderCell: (params: GridRenderCellParams<string, DecoratedStockLot>) =>
      renderQuantity(params.row.quant || 0, params.row.quantCertain),
    permissionScope: { app: ALL_APPS.STOCK.id },
  },
];
